/**
 * Consent Store — Gestione consenso cookie GDPR.
 *
 * Categorie:
 *   - necessary  (sempre attivo, readonly)
 *   - analytics  (GA4)
 *   - marketing  (Meta Pixel, Google Ads)
 *
 * Cookie: ik_consent — JSON con versione, categorie, timestamp.
 * Bridge legacy: scrive anche cc_cookie per compatibilità con Vanilla Cookie Consent.
 *
 * Uso nei template:
 *   $store.consent.bannerVisible        — mostra il banner
 *   $store.consent.preferencesOpen      — mostra il pannello preferenze
 *   $store.consent.acceptAll()          — accetta tutto
 *   $store.consent.rejectNonEssential() — solo necessari
 *   $store.consent.savePreferences()    — salva scelte custom
 *   $store.consent.openPreferences()    — apre preferenze (es. da link footer)
 *   $store.consent.hasConsent('analytics') — check singola categoria
 */
export function registerConsentStore(Alpine) {
    const COOKIE_NAME    = 'ik_consent';
    const COOKIE_VERSION = 1;
    const COOKIE_MAX_AGE = 15724800; // 182 giorni in secondi

    Alpine.store('consent', {
        bannerVisible: false,
        preferencesOpen: false,
        decided: false,
        categories: {
            necessary: true,
            analytics: false,
            marketing: false,
        },

        init() {
            const saved = this._readCookie();
            if (saved && saved.v === COOKIE_VERSION && saved.c) {
                this.categories.necessary = true;
                this.categories.analytics = !!saved.c.analytics;
                this.categories.marketing = !!saved.c.marketing;
                this.decided = true;
            } else {
                this.bannerVisible = true;
            }
        },

        acceptAll() {
            this.categories.analytics = true;
            this.categories.marketing = true;
            this._apply('accept_all');
        },

        rejectNonEssential() {
            this.categories.analytics = false;
            this.categories.marketing = false;
            this._apply('reject');
        },

        savePreferences() {
            this._apply('custom');
            this.preferencesOpen = false;
        },

        openPreferences() {
            this.preferencesOpen = true;
        },

        closePreferences() {
            this.preferencesOpen = false;
        },

        hasConsent(category) {
            return !!this.categories[category];
        },

        // ── Internals ──────────────────────────────────────

        _apply(action) {
            this.decided = true;
            this.bannerVisible = false;
            this.preferencesOpen = false;
            this._saveCookie();
            this._pushConsentUpdate();
            this._writeLegacyCookie();
            this._logConsent(action);
            // Dopo l'update del consenso: chi accetta viene contato da GA4 con
            // il cookie; chi rifiuta resta visibile solo nel consent-log.
            // Letto da tracking/hooks.js (evento cookie_consent_choice).
            try {
                window.dispatchEvent(new CustomEvent('ik:consent-choice', {
                    detail: {
                        action,
                        analytics: this.categories.analytics,
                        marketing: this.categories.marketing,
                        seconds_to_choice: Math.round(performance.now() / 1000),
                    },
                }));
            } catch {
                // Silenzioso
            }
        },

        _pushConsentUpdate() {
            if (window.ikTracking?.updateConsent) {
                window.ikTracking.updateConsent(this.categories);
            } else if (typeof gtag === 'function') {
                // Fallback diretto se tracking module non ancora caricato
                gtag('consent', 'update', {
                    'analytics_storage':   this.categories.analytics ? 'granted' : 'denied',
                    'ad_storage':          this.categories.marketing ? 'granted' : 'denied',
                    'ad_user_data':        this.categories.marketing ? 'granted' : 'denied',
                    'ad_personalization':  this.categories.marketing ? 'granted' : 'denied',
                });
            }
        },

        _saveCookie() {
            const data = JSON.stringify({
                v:  COOKIE_VERSION,
                c:  { necessary: true, analytics: this.categories.analytics, marketing: this.categories.marketing },
                ts: Math.floor(Date.now() / 1000),
            });
            document.cookie = COOKIE_NAME + '=' + encodeURIComponent(data)
                + ';path=/;max-age=' + COOKIE_MAX_AGE + ';SameSite=Lax';
        },

        _readCookie() {
            const match = document.cookie.match(new RegExp('(?:^|; )' + COOKIE_NAME + '=([^;]*)'));
            if (!match) return null;
            try {
                return JSON.parse(decodeURIComponent(match[1]));
            } catch {
                return null;
            }
        },

        /**
         * Bridge legacy: scrive cc_cookie nel formato Vanilla Cookie Consent
         * per compatibilità con il frontend legacy.
         */
        _writeLegacyCookie() {
            const accepted = ['necessary'];
            if (this.categories.analytics) accepted.push('analytics');
            if (this.categories.marketing) accepted.push('marketing');

            const ccData = JSON.stringify({
                categories: accepted,
                level: accepted,
                revision: 0,
                rfc_cookie: false,
            });
            document.cookie = 'cc_cookie=' + encodeURIComponent(ccData)
                + ';path=/;max-age=' + COOKIE_MAX_AGE + ';SameSite=Lax';
        },

        /**
         * Log consent server-side per audit GDPR (Art. 7.1).
         * Fire & forget — non blocca l'UI.
         */
        _logConsent(action) {
            const ajaxBase = window.IKOS?.ajaxBase || '/ajax';
            try {
                navigator.sendBeacon?.(ajaxBase + '/consent-log.php', JSON.stringify({
                    action,
                    categories: this.categories,
                    version: COOKIE_VERSION,
                    url: window.location.href,
                }));
            } catch {
                // Silenzioso — il logging non deve mai rompere l'UX
            }
        },
    });
}
